import { useState, useEffect } from 'react';
import { getTokenBalances } from '../util/helpers';

function parseDataValue(dataValue) {
  if (!dataValue || typeof dataValue !== 'string') return { chainId: null, address: '' };
  const [rawChainId, rawAddress] = dataValue.split(':');
  const chainId = Number(rawChainId);
  return {
    chainId: Number.isFinite(chainId) && chainId > 0 ? chainId : null,
    address: rawAddress || '',
  };
}

/**
 * Fetches balance for the currently selected input token so the swap form can show it
 * without requiring the user to open the token list.
 * @param {{ dataValue?: string } | null} selectedToken - Token object with dataValue (chainId:address:...)
 * @param {string | null} userAddress - Connected wallet address
 * @returns {{ balance: string, loading: boolean }}
 */
export function useSelectedTokenBalance(selectedToken, userAddress) {
  const [balance, setBalance] = useState('0');
  const [loading, setLoading] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const dataValue = selectedToken?.dataValue;
  const parsed = parseDataValue(dataValue || '');
  const chainId = selectedToken?.chainId || parsed.chainId;
  const address = selectedToken?.address || parsed.address;
  const canFetch = Boolean(userAddress && chainId && address);

  useEffect(() => {
    if (!canFetch) {
      setBalance('0');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTokenBalances([address], userAddress, Number(chainId))
      .then((map) => {
        if (cancelled) return;
        const key = address.toLowerCase();
        setBalance(map[key] ?? '0');
      })
      .catch(() => {
        if (!cancelled) setBalance('0');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canFetch, userAddress, chainId, address, refreshIndex]);

  return { balance, loading, refresh: () => setRefreshIndex((prev) => prev + 1) };
}
